import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Modal, Select, Space, Tag, Tooltip, message } from "antd";
import axAPI from "../../api/axios-instance";

/**
 * Маппинг комментаторов BMS на профили CMS (fnl-11911).
 *
 * Словарь имён приходит из назначений BMS (id у них нет — только имя
 * и фамилия), наполняется синком автоматически. Здесь админ привязывает
 * запись словаря к профилю; автосинк такую связь считает главнее
 * совпадения по точному ФИО.
 */

type BmsCommentator = {
  id: number;
  firstName: string;
  lastName: string;
  commentatorId: number | null;
  commentatorFirstName: string | null;
  commentatorLastName: string | null;
  lastSeenAt: string | null;
};

const QUERY_KEY = ["bmsCommentators"];

function useBmsCommentators() {
  return useQuery<BmsCommentator[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data } = await axAPI.get("cms/bms/commentator/list");
      return data;
    },
  });
}

export function BmsMappingCell({ commentatorId }: { commentatorId: number }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBmsId, setSelectedBmsId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const { data: bmsCommentators, isLoading } = useBmsCommentators();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const mapMutation = useMutation({
    mutationFn: (bmsCommentatorId: number) =>
      axAPI.post("cms/bms/commentator/mapping", null, {
        params: { bmsCommentatorId, commentatorId },
      }),
    onSuccess: () => {
      message.success("Комментатор BMS привязан");
      setModalOpen(false);
      setSelectedBmsId(null);
      invalidate();
    },
    onError: () => message.error("Не удалось привязать"),
  });

  const unmapMutation = useMutation({
    mutationFn: (bmsCommentatorId: number) =>
      axAPI.post("cms/bms/commentator/mapping/delete", null, {
        params: { bmsCommentatorId },
      }),
    onSuccess: () => {
      message.success("Привязка снята");
      invalidate();
    },
    onError: () => message.error("Не удалось снять привязку"),
  });

  // Записи BMS, привязанные к этому профилю. Их может быть несколько:
  // в BMS одного человека могли завести под разными написаниями имени.
  const mapped = useMemo(
    () =>
      (bmsCommentators ?? []).filter(
        (bms) => bms.commentatorId === commentatorId
      ),
    [bmsCommentators, commentatorId]
  );

  // В выбор даём только свободные записи — перекидывать чужую привязку
  // нужно осознанно, через снятие на её строке.
  const unmappedOptions = useMemo(
    () =>
      (bmsCommentators ?? [])
        .filter((bms) => bms.commentatorId == null)
        .map((bms) => ({
          value: bms.id,
          label: `${bms.lastName} ${bms.firstName}`,
        })),
    [bmsCommentators]
  );

  if (isLoading) {
    return null;
  }

  return (
    <Space direction="vertical" size={4}>
      {mapped.map((bms) => (
        <Tooltip key={bms.id} title="Нажмите, чтобы снять привязку">
          <Tag
            closable
            onClose={(event) => {
              event.preventDefault();
              unmapMutation.mutate(bms.id);
            }}
          >
            {bms.lastName} {bms.firstName}
          </Tag>
        </Tooltip>
      ))}

      <Button
        size="small"
        onClick={() => setModalOpen(true)}
        disabled={!unmappedOptions.length}
      >
        Смаппить
      </Button>
      {!unmappedOptions.length && !mapped.length && (
        <Tooltip title="Словарь наполняется автоматически из назначений BMS. Свободных записей сейчас нет.">
          <span style={{ color: "#999", fontSize: 12 }}>нет записей BMS</span>
        </Tooltip>
      )}

      <Modal
        title="Привязать комментатора BMS"
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setSelectedBmsId(null);
        }}
        onOk={() => selectedBmsId != null && mapMutation.mutate(selectedBmsId)}
        okText="Привязать"
        cancelText="Отмена"
        okButtonProps={{
          disabled: selectedBmsId == null,
          loading: mapMutation.isLoading,
        }}
      >
        <Select
          style={{ width: "100%" }}
          placeholder="Комментатор из BMS"
          showSearch
          optionFilterProp="label"
          value={selectedBmsId}
          onChange={setSelectedBmsId}
          options={unmappedOptions}
        />
      </Modal>
    </Space>
  );
}
